import { useEffect, useRef, useState } from "react";

/*
  A scroll-triggered reveal that fails *visible*.

  framer's `whileInView` fails two ways on this page, both measured rather than
  theorised (issue 10):

  - an IntersectionObserver that never reports leaves every staggered
    descendant parked on the `hidden` variant — 37 of them sat below 5%
    opacity, present in the DOM, readable by nothing;
  - no IntersectionObserver at all makes framer throw out of render, and one
    section paints while the rest of the page does not.

  Both share a cause: the entrance owns whether the content exists visually. It
  does not here. The observer decides *when* a section animates in; it never
  decides *whether* the section can be read. When the observer is missing,
  throws, or is deaf, the section is revealed anyway and the reader loses an
  animation rather than a page.

  Distinguishing "deaf observer" from "section is simply further down the page"
  is the one subtle part: a real IntersectionObserver always reports an initial
  state on `observe`, on screen or not. So a callback that has not fired at all
  by `DEAF_MS` is a broken observer, not a patient one.
*/

/** How long to wait for the observer's first report before giving up on it. */
const DEAF_MS = 400;

/** Matches the `viewport.margin` the landing sections animated in with. */
const MARGIN = "-80px";

export function useSectionReveal<T extends Element>(margin: string = MARGIN) {
  const ref = useRef<T | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (revealed) return;

    const node = ref.current;
    let observer: IntersectionObserver | undefined;
    let heard = false;

    if (node && typeof IntersectionObserver === "function") {
      try {
        observer = new IntersectionObserver(
          (entries) => {
            heard = true;
            if (entries.some((entry) => entry.isIntersecting)) setRevealed(true);
          },
          { rootMargin: margin }
        );
        observer.observe(node);
      } catch {
        observer = undefined;
      }
    }

    if (!observer) {
      setRevealed(true);
      return;
    }

    const deaf = setTimeout(() => {
      if (!heard) setRevealed(true);
    }, DEAF_MS);

    return () => {
      clearTimeout(deaf);
      observer.disconnect();
    };
  }, [margin, revealed]);

  return [ref, revealed] as const;
}
